import React, { useEffect, useState } from "react";
import { Button, useMediaQuery, useTheme } from "@mui/material";
import { MbscCalendarEvent } from "@mobiscroll/react";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { SeriesBookingScope } from "../../features/Schedule/series-booking.types";
import { useRemoteInactivateBookings } from "../../hooks/Remote/Booking/useRemoteInactivateBookings";
import { useRemoteInactivateSeriesBookings } from "../../hooks/Remote/Booking/useRemoteInactivateSeriesBookings";
import useTimezone from "../../hooks/useTimezone/useTimezone";
import { BookingRequestsStatus } from "../BookingRequest/typings/booking-requests.types";
import { bookingHasSeriesId } from "./functions/booking-has-series-id.function";
import { conferenceRoomHasVisitorRegistrations } from "./functions/conference-room-has-visitor-registrations.function";
import { isScheduleTimeLockedByConferenceServiceSetup } from "./functions/isScheduleTimeLockedByConferenceServiceSetup.function";
import { ScheduleCancelConferenceVisitorsDialog } from "./schedule-cancel-conference-visitors-dialog.component";
import { ScheduleSeriesScopeDialog } from "./schedule-series-scope-dialog.component";

type P = {
  schedule: BookingScheduleInterface;
  calendarData: MbscCalendarEvent[];
  isEditable: boolean;
  onClose: () => void;
  /** When true, hides cancel booking (computed once at dialog / popover level). */
  isConferenceServiceSetupLock?: boolean;
  onCalendarDataChange?: (events: MbscCalendarEvent[]) => void;
  setDeletingBookings?: (d: boolean) => void;
  handleRejectBookings?: () => void;
  handleApproveBookings?: () => void;
  /** Refetch calendar after room-only cancel so new visit-only tiles appear (Q11b). */
  refetchAllSchedule?: () => void;
};

export const ScheduleEventDeleteButton: React.FC<P> = ({
  schedule,
  calendarData,
  isEditable,
  isConferenceServiceSetupLock = false,
  onClose,
  onCalendarDataChange,
  setDeletingBookings,
  handleRejectBookings,
  handleApproveBookings,
  refetchAllSchedule
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const timezoneState = useSelector((state: RootState) => state.login.settings?.timezone);
  const deviceTimezone = useTimezone();
  const [cancelVisitorsDialogOpen, setCancelVisitorsDialogOpen] = useState(false);
  const [seriesScopeDialogOpen, setSeriesScopeDialogOpen] = useState(false);
  const [pendingSeriesScope, setPendingSeriesScope] = useState<SeriesBookingScope | null>(null);
  const [roomOnlyCancelPending, setRoomOnlyCancelPending] = useState(false);

  const { mutate: inactivateBookings, status: inactivateBookingsStatus } =
    useRemoteInactivateBookings();
  const { mutate: inactivateSeriesBookings, status: inactivateSeriesStatus } =
    useRemoteInactivateSeriesBookings();

  const isSeries = bookingHasSeriesId(schedule.seriesId);

  const submitSingleInactivate = (cancelVisitorRegistrations?: boolean) => {
    setDeletingBookings?.(true);
    const entry = {
      deleted: false,
      bookingIds: [schedule.id],
      bookingType: schedule.bookingType as BookingType,
      ...(cancelVisitorRegistrations === true ? { cancelVisitorRegistrations: true } : {})
    };
    inactivateBookings({ bookings: [entry] });
  };

  const submitSeriesInactivate = (
    scope: SeriesBookingScope,
    cancelVisitorRegistrations?: boolean
  ) => {
    setDeletingBookings?.(true);
    inactivateSeriesBookings({
      bookingId: schedule.id,
      bookingType: schedule.bookingType as BookingType,
      scope,
      deleted: false,
      ...(cancelVisitorRegistrations === true ? { cancelVisitorRegistrations: true } : {})
    });
  };

  const handleInactiveBookings = () => {
    const timezone = timezoneState === schedule.timezone ? schedule.timezone : deviceTimezone;
    const isCancellationLockedNow = isScheduleTimeLockedByConferenceServiceSetup({
      schedule,
      timezone,
      now: DateTime.now()
    });

    if (isCancellationLockedNow) {
      enqueueSnackbar(t("_schedule_cancelBookingBlockedBySetupTime"), {
        variant: "warning"
      });
      return;
    }

    if (isSeries) {
      setSeriesScopeDialogOpen(true);
      return;
    }

    if (conferenceRoomHasVisitorRegistrations(schedule)) {
      setCancelVisitorsDialogOpen(true);
      return;
    }

    submitSingleInactivate();
  };

  const handleSeriesScopeConfirm = (scope: SeriesBookingScope) => {
    setSeriesScopeDialogOpen(false);
    if (conferenceRoomHasVisitorRegistrations(schedule)) {
      setPendingSeriesScope(scope);
      setCancelVisitorsDialogOpen(true);
      return;
    }
    submitSeriesInactivate(scope);
  };

  const handleCancelVisitorsDialogClose = () => {
    setCancelVisitorsDialogOpen(false);
    setPendingSeriesScope(null);
  };

  const handleCancelVisitorsDialogConfirm = (cancelVisitorRegistrations: boolean) => {
    setCancelVisitorsDialogOpen(false);
    const seriesScope = pendingSeriesScope;
    setPendingSeriesScope(null);

    if (seriesScope != null) {
      if (cancelVisitorRegistrations) {
        submitSeriesInactivate(seriesScope, true);
        return;
      }
      setRoomOnlyCancelPending(true);
      submitSeriesInactivate(seriesScope);
      return;
    }

    if (cancelVisitorRegistrations) {
      submitSingleInactivate(true);
      return;
    }
    setRoomOnlyCancelPending(true);
    submitSingleInactivate();
  };

  const handleInactivateSuccess = () => {
    const shouldRefetchAfterRoomOnlyCancel = roomOnlyCancelPending;
    setRoomOnlyCancelPending(false);

    if (isSeries) {
      refetchAllSchedule?.();
      onClose();
      setDeletingBookings?.(false);
      return;
    }

    const index = calendarData.findIndex(c => c.id === schedule.id);
    if (index >= 0) {
      const newCalendarData = calendarData.slice();
      newCalendarData.splice(index, 1);
      onCalendarDataChange?.(newCalendarData);
    }

    if (index < 0 || shouldRefetchAfterRoomOnlyCancel) {
      refetchAllSchedule?.();
    }

    onClose();
    setDeletingBookings?.(false);
  };

  useEffect(() => {
    if (inactivateBookingsStatus === "success" || inactivateSeriesStatus === "success") {
      handleInactivateSuccess();
    }
  }, [inactivateBookingsStatus, inactivateSeriesStatus]);

  return (
    <>
      {isEditable && !isConferenceServiceSetupLock && (
        <Button
          color={"error"}
          sx={{ padding: "7px 10px 7px 10px" }}
          onClick={handleInactiveBookings}
          data-testid="schedule-item-delete-btn"
        >
          {isMobile ? t("Cancel Booking_short") : t("Cancel Booking")}
        </Button>
      )}
      {!isEditable && schedule.status !== BookingRequestsStatus.REJECTED && (
        <>
          <Button
            color={"error"}
            sx={{ padding: "7px 10px 7px 10px" }}
            onClick={handleRejectBookings}
            data-testid="schedule-item-reject-btn"
          >
            {t("Reject")}
          </Button>
          <Button
            sx={{ padding: "7px 10px 7px 10px" }}
            disabled={schedule.status === BookingRequestsStatus.APPROVED}
            onClick={handleApproveBookings}
            data-testid="schedule-item-approve-btn"
          >
            {t("Approve")}
          </Button>
        </>
      )}

      <ScheduleSeriesScopeDialog
        open={seriesScopeDialogOpen}
        selectedDateIso={schedule.start ?? schedule.startDate}
        timezone={schedule.timezone}
        bookingId={schedule.id}
        bookingType={schedule.bookingType}
        titleKey="_schedule_seriesCancelScopeTitle"
        bodyKey="_schedule_seriesCancelScopeBody"
        confirmLabelKey="Cancel Booking"
        confirmColor="error"
        onClose={() => setSeriesScopeDialogOpen(false)}
        onConfirm={handleSeriesScopeConfirm}
      />

      <ScheduleCancelConferenceVisitorsDialog
        open={cancelVisitorsDialogOpen}
        onClose={handleCancelVisitorsDialogClose}
        onConfirm={handleCancelVisitorsDialogConfirm}
      />
    </>
  );
};
